import PublicationDetails from '../Publications/PublicationDetails';
import React, { useRef, useEffect, useState, useDeferredValue, useMemo } from "react";
import "../../styles/PropertyList.css";
import PropertyReport from "./PropertyReport";
import { Dropdown } from "primereact/dropdown";
import { Sidebar } from "primereact/sidebar";
import { formatPropertyPrice, formatPublisherName, pricePerSquareMeter } from "../Map/propertyMap";

const COMPARABLE_GUIDE = {
  title: "Cómo funciona",
  lead: "Dejá activos solo inmuebles muy parecidos al tuyo. Mejor pocos y precisos que muchos mezclados.",
  chips: ["Misma zona", "m² parecidos", "Ambientes similares", "Estado comparable"],
  steps: [
    "Dejá activas entre 2 y 12 propiedades que compitan en la misma zona.",
    "Compará m², ambientes, dormitorios, baños, antigüedad y amenities.",
    "Desactivá las que estén lejos, sean otra tipología o no representen el estado real.",
    "Usá Filtros si necesitás acercar la muestra a lo más importante del inmueble.",
  ],
  tip: "Pocas propiedades muy parecidas = tasación más fina.",
};

/** Si hay dirección, no repetimos barrio/localidad debajo. */
const getCardLocation = (property) => {
  const address = (property.address || "").trim();
  const location = (property.location || "").trim();
  if (address) return null;
  return location || null;
};

const SkeletonLoader = () => {
  return (
    <div className="property-card skeleton">
      <div className="property-thumbnail skeleton-box"></div>
      <div className="property-details">
        <div className="skeleton-box skeleton-title"></div>
        <div className="skeleton-box skeleton-text"></div>
        <div className="skeleton-box skeleton-text"></div>
        <div className="skeleton-box skeleton-price"></div>
        <div className="skeleton-box skeleton-price"></div>
        <div className="skeleton-box skeleton-price"></div>
      </div>
    </div>
  );
};

const PropertyList = ({
  properties,
  loading,
  error,
  selectedProperty,
  listOpen = true,
  onPropertyClick,
  visitedProperties,
  wizardData,
  hasZone = false,
  refreshing = false,
  inactiveProperties = [],
  onToggleInactive = () => {},
  onSelectPublication,
  selectionPending = false,
}) => {
  const inactiveSet = new Set(inactiveProperties);
  // El estimado puede ir un frame atrás; las cards deben reaccionar al toque.
  const deferredInactive = useDeferredValue(inactiveProperties);
  const deferredInactiveSet = useMemo(
    () => new Set(deferredInactive),
    [deferredInactive],
  );
  const [sortedProperties, setSortedProperties] = useState(properties);
  const [sortOrder, setSortOrder] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const selectedPropertyRef = useRef(null);

  const sortOptions = [
    { label: "Precio Ascendente", value: "asc" },
    { label: "Precio Descendente", value: "desc" },
  ];

  useEffect(() => {
    const card = selectedPropertyRef.current;
    const scroller = card?.closest(".home-properties__content");
    if (listOpen && selectedProperty && card && scroller) {
      // scrollIntoView also scrolls clipped ancestors, revealing a closed panel.
      scroller.scrollTo({
        top: scroller.scrollTop + card.getBoundingClientRect().top
          - scroller.getBoundingClientRect().top - (scroller.clientHeight - card.offsetHeight) / 2,
        behavior: "smooth",
      });
    }
  }, [selectedProperty, listOpen, sortedProperties]);

  useEffect(() => {
    let sorted = [...properties];
    if (sortOrder === "asc") {
      sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortOrder === "desc") {
      sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    }
    setSortedProperties(sorted);
  }, [properties, sortOrder]);

  if (loading) {
    return (
      <div className="property-list">
        {[...Array(5)].map((_, i) => (
          <SkeletonLoader key={i} />
        ))}
      </div>
    );
  }

  if (error || properties.length === 0) {
    const title = error
      ? "No pudimos cargar las propiedades"
      : refreshing
        ? "Actualizando la búsqueda"
        : hasZone
          ? "No hay comparables con estos filtros"
          : "Todavía no hay una zona de búsqueda";
    const body = error
      ? "Probá de nuevo en unos segundos."
      : refreshing
        ? "Estamos actualizando la búsqueda. Los comparables aparecen acá en cuanto estén listos."
        : hasZone
          ? "Ajustá los filtros a mano desde el menú Filtros y conservá los que definan la búsqueda."
          : "Definí la zona en un informe nuevo para ver propiedades comparables.";
    return (
      <div className="property-list-empty" role={error ? "alert" : "status"}>
        <div className="property-list-empty__icon" aria-hidden="true">
          <i className={`pi ${error ? "pi-exclamation-circle" : "pi-search"}`} />
        </div>
        <h2>{title}</h2>
        <p>{body}</p>
      </div>
    );
  }

  const MIN_PROPS = 2;
  const MAX_PROPS = 12;
  const activeCount = sortedProperties.reduce(
    (n, p) => n + (inactiveSet.has(p.id) ? 0 : 1),
    0,
  );
  const reportProperties = sortedProperties.filter(
    (p) => !deferredInactiveSet.has(p.id),
  );
  const underLimit = activeCount < MIN_PROPS;
  const overLimit = activeCount > MAX_PROPS;
  const badgeWarning = underLimit || overLimit;

  return (
    <div className="property-list-container">
      <div className="property-list-toolbar">
        <div className="property-list-toolbar__status">
          <div
            className={`property-count-badge ${
              badgeWarning
                ? "property-count-badge--warning"
                : "property-count-badge--ok"
            }`}
          >
            <span className="property-count-badge__icon" aria-hidden="true">
              <i
                className={`pi ${
                  badgeWarning ? "pi-exclamation-triangle" : "pi-check-circle"
                }`}
              />
            </span>
            <div className="property-count-badge__text">
              <span className="property-count-badge__count">
                {activeCount}/{MAX_PROPS} activas
              </span>
              {underLimit && (
                <span className="property-count-badge__hint">
                  Mín. {MIN_PROPS} para generar
                </span>
              )}
              {overLimit && (
                <span className="property-count-badge__hint">
                  Sacá {activeCount - MAX_PROPS}
                </span>
              )}
              {!badgeWarning && (
                <span className="property-count-badge__hint property-count-badge__hint--ok">
                  Listo para generar
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="property-list-help-trigger"
            onClick={() => setHelpOpen(true)}
            aria-label="Cómo funciona"
            title="Cómo funciona"
          >
            <i className="pi pi-question-circle" aria-hidden="true" />
            <span className="property-list-help-trigger__label">
              Cómo funciona
            </span>
          </button>
        </div>

        <PropertyReport
          properties={reportProperties}
          wizardData={wizardData}
          underLimit={underLimit}
          overLimit={overLimit}
          minProps={MIN_PROPS}
          maxProps={MAX_PROPS}
          selectionPending={selectionPending}
          sortControl={
            <Dropdown
              value={sortOrder}
              options={sortOptions}
              onChange={(e) => setSortOrder(e.value)}
              placeholder="Ordenar por precio"
              className="sort-dropdown"
              panelClassName="sort-dropdown-panel"
            />
          }
        />
      </div>

      <Sidebar
        visible={helpOpen}
        position="right"
        onHide={() => setHelpOpen(false)}
        className="comparable-help-drawer"
        showCloseIcon
        dismissable
        modal
        baseZIndex={1300}
        aria-label={COMPARABLE_GUIDE.title}
      >
        <div className="comparable-help-drawer__body">
          <div className="comparable-help-drawer__header">
            <span className="comparable-help-drawer__icon" aria-hidden="true">
              <i className="pi pi-check-square" />
            </span>
            <div>
              <p className="comparable-help-drawer__eyebrow">Guía rápida</p>
              <h2 className="comparable-help-drawer__title">
                {COMPARABLE_GUIDE.title}
              </h2>
            </div>
          </div>
          <p className="comparable-help-drawer__lead">{COMPARABLE_GUIDE.lead}</p>
          <div className="comparable-help-drawer__chips">
            {COMPARABLE_GUIDE.chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
          <ol className="comparable-help-drawer__steps">
            {COMPARABLE_GUIDE.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <div className="comparable-help-drawer__tip">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{COMPARABLE_GUIDE.tip}</span>
          </div>
        </div>
      </Sidebar>

      <div className="property-list-scroll">
        {sortedProperties.map((property) => {
          const isInactive = inactiveSet.has(property.id);
          const cardLocation = getCardLocation(property);
          const cardTitle = property.address || property.location || "Sin dirección";
          const publisherName = formatPublisherName(property.owner_type);
          const amenityItems = [
            property.hasPool && { key: "pool", label: "Pileta" },
            property.hasGarden && { key: "garden", label: "Jardín" },
            property.hasGrill && { key: "grill", label: "Parrilla" },
            property.hasTerrace && { key: "terrace", label: "Terraza" },
            property.hasBalcony && { key: "balcony", label: "Balcón" },
            property.parking && { key: "parking", label: "Cochera" },
          ].filter(Boolean);
          const pricePerM2 = pricePerSquareMeter(
            property.price,
            property.square_meters_covered
          );

          return (
            <div
              className={`property-card${
                !isInactive ? " property-card--active" : " property-card--inactive"
              }${selectedProperty?.id === property.id ? " selected" : ""}`}
              key={property.property_group_key || property.id}
              ref={
                selectedProperty?.id === property.id
                  ? selectedPropertyRef
                  : null
              }
              onClick={() => onToggleInactive(property.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleInactive(property.id);
                }
              }}
              title={
                isInactive
                  ? "Clic para activar e incluir en el informe"
                  : "Clic para desactivar (no se incluye en el informe)"
              }
            >
              <img
                src={property.image_url}
                alt={`Propiedad en ${cardTitle}`}
                className="property-thumbnail"
                loading="lazy"
                decoding="async"
              />
              <div className="property-details">
                <div className="property-card-top">
                  <div className="property-card-identity">
                    <h3 className="property-address">{cardTitle}</h3>
                    {cardLocation && (
                      <p className="property-location">{cardLocation}</p>
                    )}
                    {publisherName && (
                      <p className="property-publisher" title={publisherName}>
                        <i className="pi pi-building" aria-hidden="true" />
                        <span>
                          Publica:{" "}
                          <span className="property-publisher__name">{publisherName}</span>
                        </span>
                      </p>
                    )}
                  </div>
                  <div
                    className={`property-active-switch${
                      isInactive ? " property-active-switch--inactive" : ""
                    }`}
                    aria-hidden="true"
                    title={isInactive ? "Inactiva" : "Activa"}
                  >
                    <span className="property-active-switch__track">
                      <span className="property-active-switch__thumb" />
                    </span>
                  </div>
                </div>

                <p className="property-price">
                  <span className="property-price__value">
                    {property.price_currency}{" "}
                    {property.price?.toLocaleString()}
                  </span>
                  {property.expenses_value > 0 && (
                    <span className="expenses">
                      + {property.expenses_currency}{" "}
                      {property.expenses_value?.toLocaleString()} exp
                    </span>
                  )}
                </p>

                {pricePerM2 != null && (
                  <p className="property-ppsqm">
                    <span className="property-ppsqm__value">
                      {formatPropertyPrice(pricePerM2, property.price_currency)} / m²
                    </span>
                    <span className="property-ppsqm__basis">cubierto</span>
                  </p>
                )}

                <div className="property-meta">
                  {property.rooms != null && (
                    <span>{property.rooms} amb</span>
                  )}
                  {property.bedrooms != null && (
                    <span>{property.bedrooms} dorm</span>
                  )}
                  {property.bathrooms != null && (
                    <span>
                      {property.bathrooms}{" "}
                      {property.bathrooms === 1 ? "baño" : "baños"}
                    </span>
                  )}
                  {property.square_meters_covered != null && (
                    <span>{property.square_meters_covered} m²</span>
                  )}
                </div>

                {amenityItems.length > 0 && (
                  <div className="property-amenities" aria-label="Amenities">
                    {amenityItems.map((item) => (
                      <span key={item.key} className="property-amenity">
                        {item.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <PublicationDetails property={property} onSelectPublication={onSelectPublication} selectionPending={selectionPending} />
              <div className="property-actions">
                <button
                  type="button"
                  className="property-card-action property-card-action--map"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPropertyClick(property);
                  }}
                >
                  <i className="pi pi-map-marker" aria-hidden="true" />
                  <span>Ver en mapa</span>
                </button>
                <a
                  href={property.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="property-card-action property-card-action--pub"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="pi pi-external-link" aria-hidden="true" />
                  <span>Ver publicación</span>
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyList;
